import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useEffect, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router';
import { Button, Dropdown, Form, Modal } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import type { z } from 'zod';
import { ApiError, api } from '../api/client';
import type { Option, Row } from '../api/types';
import { useCan } from '../auth/AuthContext';
import { CheckField, DateField, FormError, MobileField, MoneyField, SelectField, TextField, applyServerErrors, type AnyForm } from '../forms/fields';
import { DataTable, type Column } from '../ui/DataTable';
import { DateRange } from '../ui/DateRange';
import { useConfirm, useToast } from '../ui/feedback';
import { PageHeader } from '../ui/PageHeader';
import { useListQuery } from '../ui/useListQuery';

export type Values = Record<string, any>; // eslint-disable-line @typescript-eslint/no-explicit-any

export interface FieldDef {
  name: string;
  label: string;
  type: 'text' | 'email' | 'tel' | 'mobile' | 'money' | 'select' | 'checkbox' | 'datetime' | 'date' | 'textarea';
  /** textarea height in lines (default 3) */
  rows?: number;
  required?: boolean;
  options?: Option[];
  placeholder?: string;
  /** 'mobile' fields only: digit cap while typing (default 10). */
  maxLength?: number;
  help?: ReactNode;
  /** Bootstrap column width (1-12) on md+ screens; default 6 */
  col?: number;
  disabled?: boolean;
  hidden?: boolean;
}

export interface FilterDef {
  param: string;
  label: string;
  options: Option[];
  /** text of the "no filter" choice of a dropdown (default "<label>: all") */
  allLabel?: string;
  /** a checkbox instead of a dropdown: ticked sets the param to `value`, unticked clears it */
  type?: 'select' | 'checkbox';
  value?: string;
}

/** What a screen's own extra pieces (header buttons, a section above the list) can ask of the generic screen. */
export interface ScreenCtx {
  /** opens the New dialog, optionally with some fields already filled */
  openCreate: (initial?: Values) => void;
}

export interface CrudConfig {
  title: string;
  /** used in messages: "Delete this agent?" */
  singular: string;
  icon: string;
  menu: string;
  /** API path, e.g. '/agents' or '/transactions/agent' */
  endpoint: string;
  /** PATCH for masters, PUT for transactions (full replacement) */
  update: 'patch' | 'put';
  /** adds a "Save and new" button on a NEW record: it saves, then clears the form (keeping the dialog open) for the next one */
  saveAndNew?: boolean;
  schema: z.ZodType;
  defaults: () => Values;
  toForm: (row: Row) => Values;
  toPayload: (values: Values) => unknown;
  rowLabel: (row: Row) => string;
  dateRange?: boolean;
  /** Row actions as a single overflow (⋮) menu instead of separate edit/delete icon buttons; for dense lists. */
  compactActions?: boolean;
  pageSize?: number;
  initialSort?: { key: string; dir: 'asc' | 'desc' };
  /** hook: optional line under the page title (may call lookup hooks) */
  useSubtitle?: () => ReactNode;
  /** hook: extra buttons in the page header, before the New button */
  useHeaderActions?: (ctx: ScreenCtx) => ReactNode;
  /** hook: a note shown under the list */
  useBelow?: () => ReactNode;
  /** hook: a section shown between the header and the list's filters */
  useAbove?: (ctx: ScreenCtx) => ReactNode;
  /** hook: a longer explanation shown behind an (i) icon next to the title */
  useInfo?: () => ReactNode;
  /** hook: extra buttons for a row, shown before Edit / Delete (may call mutation hooks) */
  useRowActions?: () => (row: Row) => ReactNode;
  /** a reason why this row cannot be deleted: the Delete button is then disabled and the reason shown on hover */
  deleteBlocked?: (row: Row) => string | null;
  /** hook version of deleteBlocked, for reasons that need data (e.g. the current balance) */
  useDeleteBlocked?: () => (row: Row) => string | null;
  /** placeholder of the Search box (default "Search…") */
  searchPlaceholder?: string;
  /** closing a form that has unsaved edits asks "You have unsaved changes. Discard them?" */
  confirmDiscard?: boolean;
  /** hook: table columns and optional filter dropdowns (may call lookup hooks) */
  useTable: () => { columns: Column[]; filters?: FilterDef[] };
  /** hook: form fields (and an optional side panel) for the current form state */
  useForm: (form: AnyForm, ctx: { mode: 'create' | 'edit'; row: Row | null; readOnly: boolean }) => {
    fields: FieldDef[];
    aside?: ReactNode;
    /** replaces the aside + the plain field grid with a custom layout (built from FieldRenderer / its own controls) */
    body?: ReactNode;
    /** shown on the left of the dialog footer, e.g. a live summary of what saving will do */
    summary?: ReactNode;
    /** Shown on the right of the modal title bar, before the close button (e.g. a date field) */
    headerExtra?: ReactNode;
  };
  modalSize?: 'lg' | 'xl';
}

/** Data changed: everything cached except the signed-in user is potentially stale. */
export function invalidateData(qc: QueryClient) {
  return qc.invalidateQueries({ predicate: (q) => q.queryKey[0] !== 'me' });
}

export function FieldRenderer({ form, field, readOnly }: { form: AnyForm; field: FieldDef; readOnly: boolean }) {
  if (field.hidden) return null;
  const common = { form, name: field.name, label: field.label, help: field.help, required: field.required, disabled: readOnly || field.disabled, className: 'mb-3' };
  let control: ReactNode;
  switch (field.type) {
    case 'select':
      control = <SelectField {...common} options={field.options ?? []} placeholder={field.placeholder} />;
      break;
    case 'checkbox':
      control = <CheckField {...common} />;
      break;
    case 'money':
      control = <MoneyField {...common} placeholder={field.placeholder} />;
      break;
    case 'textarea':
      control = <TextField {...common} rows={field.rows ?? 3} />;
      break;
    case 'datetime':
      control = <DateField {...common} mode="datetime" />;
      break;
    case 'date':
      control = <DateField {...common} mode="date" />;
      break;
    case 'email':
      control = <TextField {...common} type="email" inputMode="email" />;
      break;
    case 'tel':
      control = <TextField {...common} type="tel" inputMode="tel" />;
      break;
    case 'mobile':
      control = <MobileField {...common} maxLength={field.maxLength} />;
      break;
    default:
      control = <TextField {...common} placeholder={field.placeholder} />;
  }
  return <div className={`col-12 col-md-${field.col ?? 6}`}>{control}</div>;
}

function EntityModal({ config, mode, row, canEdit, onClose, initial }: { config: CrudConfig; mode: 'create' | 'edit'; row: Row | null; canEdit: boolean; onClose: () => void; initial?: Values }) {
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const form = useForm<Values>({
    resolver: zodResolver(config.schema as never),
    defaultValues: mode === 'edit' && row ? config.toForm(row) : { ...config.defaults(), ...initial },
  });
  const readOnly = mode === 'edit' && !canEdit;
  const { fields, aside, body, summary, headerExtra } = config.useForm(form, { mode, row, readOnly });
  const [serverError, setServerError] = useState<string | null>(null);
  const [again, setAgain] = useState(false); // the next submit is a "Save and new"

  // closing with unsaved edits (Cancel, the X, Esc) asks first, when the screen opts in
  const dirty = form.formState.isDirty; // read while rendering so the form state is subscribed to
  const requestClose = async () => {
    if (config.confirmDiscard && !readOnly && dirty
      && !(await confirm({ title: 'Unsaved changes', message: 'You have unsaved changes. Discard them?', confirmLabel: 'Discard' }))) return;
    onClose();
  };

  const save = useMutation({
    mutationFn: (values: Values) => {
      const payload = config.toPayload(values);
      return mode === 'create' ? api.post(config.endpoint, payload) : api[config.update](`${config.endpoint}/${row!.id}`, payload);
    },
    onSuccess: async () => {
      await invalidateData(qc);
      toast.success(mode === 'create' ? `${cap(config.singular)} created` : `${cap(config.singular)} updated`);
      if (again) { form.reset(config.defaults()); setAgain(false); return; }
      onClose();
    },
    onError: (e) => { setAgain(false); setServerError(applyServerErrors(form, e)); },
  });

  return (
    <Modal show onHide={requestClose} size={config.modalSize ?? 'lg'} fullscreen="sm-down" backdrop="static" centered scrollable>
      <Form noValidate className="mms-modal-form" onSubmit={form.handleSubmit((v) => { setServerError(null); save.mutate(v); })}>
        <Modal.Header closeButton>
          <Modal.Title as="h2" className="h5">
            {mode === 'create' ? `New ${config.singular}` : readOnly ? cap(config.singular) : `Edit ${config.singular}`}
            {mode === 'edit' && row?.transactionNo ? <small className="text-body-secondary ms-2">{row.transactionNo}</small> : null}
            {mode === 'edit' && row?.referenceNumber ? <small className="text-body-secondary ms-2">{row.referenceNumber}</small> : null}
          </Modal.Title>
          {headerExtra && <div className="ms-auto me-4 mms-header-extra">{headerExtra}</div>}
        </Modal.Header>
        <Modal.Body>
          <FormError message={serverError} />
          {body ?? (
            <>
              {aside}
              <div className="row">
                {fields.map((f) => (
                  <FieldRenderer key={f.name} form={form} field={f} readOnly={readOnly} />
                ))}
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          {summary && <div className="me-auto small">{summary}</div>}
          <Button variant="outline-secondary" onClick={requestClose}>{readOnly ? 'Close' : 'Cancel'}</Button>
          {!readOnly && config.saveAndNew && mode === 'create' && (
            <Button type="submit" variant="outline-primary" disabled={save.isPending} onClick={() => setAgain(true)}>Save and new</Button>
          )}
          {!readOnly && (
            <Button type="submit" disabled={save.isPending} onClick={() => setAgain(false)}>
              {save.isPending ? 'Saving…' : 'Save'}
            </Button>
          )}
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

export function CrudScreen({ config }: { config: CrudConfig }) {
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const canCreate = useCan(config.menu, 'create');
  const canEdit = useCan(config.menu, 'edit');
  const canDelete = useCan(config.menu, 'delete');

  const { query, state } = useListQuery(config.endpoint, { pageSize: config.pageSize, initialSort: config.initialSort });
  const { columns, filters } = config.useTable();
  const subtitle = config.useSubtitle?.();
  const info = config.useInfo?.();
  const rowActions = config.useRowActions?.();
  const hookBlocked = config.useDeleteBlocked?.();
  const blockedReason = (row: Row): string | null => hookBlocked?.(row) ?? config.deleteBlocked?.(row) ?? null;
  const [modal, setModal] = useState<{ mode: 'create' | 'edit'; row: Row | null; initial?: Values } | null>(null);
  const ctx: ScreenCtx = { openCreate: (initial) => setModal({ mode: 'create', row: null, initial }) };
  // a link such as /accounts/purchase?new=1 (the Dashboard's quick actions) opens the New form straight away, once
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get('new') !== '1') return;
    if (canCreate) setModal({ mode: 'create', row: null });
    const next = new URLSearchParams(searchParams);
    next.delete('new');
    setSearchParams(next, { replace: true });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const headerActions = config.useHeaderActions?.(ctx);
  const above = config.useAbove?.(ctx);
  const below = config.useBelow?.();

  const del = useMutation({
    mutationFn: (row: Row) => api.delete(`${config.endpoint}/${row.id}`),
    onSuccess: async () => {
      await invalidateData(qc);
      toast.success(`${cap(config.singular)} deleted`);
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Delete failed'),
  });

  const onDelete = async (row: Row) => {
    const ok = await confirm({
      title: `Delete ${config.singular}`,
      message: <>Delete <strong>{config.rowLabel(row)}</strong>? This cannot be undone from the app.</>,
      confirmLabel: 'Delete',
    });
    if (ok) del.mutate(row);
  };

  return (
    <>
      <PageHeader
        title={config.title}
        subtitle={subtitle}
        info={info}
        icon={config.icon}
        actions={<>{headerActions}{canCreate && <Button onClick={() => setModal({ mode: 'create', row: null })}><i className="bi bi-plus-lg me-1" aria-hidden />New {config.singular}</Button>}</>}
      />
      {above}

      <div className="card mb-3">
        <div className="card-body py-2 d-flex flex-wrap gap-2 align-items-center">
          <Form.Control size="sm" type="search" placeholder={config.searchPlaceholder ?? 'Search…'} aria-label="Search" value={state.q} onChange={(e) => state.setQ(e.target.value)} style={{ maxWidth: 260 }} />
          {filters?.map((f) => f.type === 'checkbox' ? (
            <Form.Check
              key={f.param} type="checkbox" id={`filter-${f.param}`} label={f.label}
              checked={String(state.filters[f.param] ?? '') === (f.value ?? 'true')}
              onChange={(e) => state.setFilter(f.param, e.target.checked ? (f.value ?? 'true') : '')}
            />
          ) : (
            <Form.Select key={f.param} size="sm" aria-label={f.label} value={String(state.filters[f.param] ?? '')} onChange={(e) => state.setFilter(f.param, e.target.value)} style={{ width: 'auto', maxWidth: 220 }}>
              <option value="">{f.allLabel ?? `${f.label}: all`}</option>
              {f.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </Form.Select>
          ))}
          {config.dateRange && (
            <DateRange
              value={{ from: String(state.filters.from ?? ''), to: String(state.filters.to ?? '') }}
              onChange={(r) => { state.setFilter('from', r.from); state.setFilter('to', r.to); }}
            />
          )}
        </div>
      </div>

      {query.isError && (
        <div className="alert alert-danger" role="alert">
          {query.error instanceof ApiError ? query.error.message : 'Could not load data'}
          <Button size="sm" variant="link" onClick={() => query.refetch()}>Retry</Button>
        </div>
      )}

      <div className="card">
        <div className="card-body p-2 p-md-3">
          <DataTable
            columns={columns}
            rows={query.data?.data}
            loading={query.isPending}
            fetching={query.isFetching}
            sort={state.sort}
            dir={state.dir}
            onSort={state.toggleSort}
            page={state.page}
            pageSize={state.pageSize}
            total={query.data?.total}
            onPage={state.setPage}
            caption={config.title}
            actions={(row) => config.compactActions ? (
              <Dropdown align="end">
                <Dropdown.Toggle size="sm" variant="outline-secondary" className="mms-action-toggle" aria-label={`Actions for ${config.rowLabel(row)}`}>
                  <i className="bi bi-three-dots" aria-hidden />
                </Dropdown.Toggle>
                <Dropdown.Menu role="menu">
                  <Dropdown.Item role="menuitem" onClick={() => setModal({ mode: 'edit', row })}>
                    <i className={`bi ${canEdit ? 'bi-pencil' : 'bi-eye'} me-2`} aria-hidden />{canEdit ? 'Edit' : 'View'}
                  </Dropdown.Item>
                  {canDelete && (
                    <Dropdown.Item role="menuitem" className="text-danger" onClick={() => onDelete(row)} disabled={del.isPending || !!blockedReason(row)} title={blockedReason(row) ?? undefined}>
                      <i className="bi bi-trash me-2" aria-hidden />Delete
                    </Dropdown.Item>
                  )}
                </Dropdown.Menu>
              </Dropdown>
            ) : (
              <>
                {rowActions?.(row)}
                <Button size="sm" variant="outline-secondary" className="me-1" onClick={() => setModal({ mode: 'edit', row })} aria-label={`${canEdit ? 'Edit' : 'View'} ${config.rowLabel(row)}`}>
                  <i className={`bi ${canEdit ? 'bi-pencil' : 'bi-eye'}`} aria-hidden />
                </Button>
                {canDelete && (
                  <span className="d-inline-block" title={blockedReason(row) ?? undefined}>
                    <Button size="sm" variant="outline-danger" onClick={() => onDelete(row)} disabled={del.isPending || !!blockedReason(row)} aria-label={`Delete ${config.rowLabel(row)}`}>
                      <i className="bi bi-trash" aria-hidden />
                    </Button>
                  </span>
                )}
              </>
            )}
          />
        </div>
      </div>

      {below}

      {modal && <EntityModal config={config} mode={modal.mode} row={modal.row} initial={modal.initial} canEdit={canEdit} onClose={() => setModal(null)} />}
    </>
  );
}
